import { c as _c } from "react/compiler-runtime";
import cx from 'classix';
import { useLocale, useTranslations } from 'next-intl';
import rootPageSharedStyles from '@/app/[locale]/root-page-share.module.scss';
import imagesLoaderCDN from '@/configs/images-loader-cdn';
import { DEFAULT_LISTING_PART_VARIANTS } from '@/features/listing/constants/listing-constants';
import { routing } from '@/i18n/routing';
import { US_GALLERY_CITIES } from '@/sections/cities-gallery/constants/cities-gallery-constants';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import styles from './cities-gallery-section.module.scss';
import { WaveSymbolDefinition, WaveSymbolUse } from './wave-symbol';
interface CitiesGallerySectionProps {
  title: string;
}

// Card widths per breakpoint (2 / 3 / 4 / 5 columns of a 1330px max grid), so
// the CDN is asked for the size actually painted instead of a flat 30vw.
const IMAGE_SIZES = '(max-width: 359px) 50vw, (max-width: 567px) 33vw, (max-width: 767px) 25vw, 260px';

// Two of the widths next/image would offer for that `sizes` string (from
// next.config's imageSizes/deviceSizes): 320 covers 1x and most 2x phone cards,
// 640 covers 2x desktop cards. Every extra candidate is another CDN URL in the
// HTML and again in the RSC payload, twenty-five times.
const SRCSET_WIDTHS = [320, 640] as const;
const IMAGE_QUALITY = 75;
function buildSrcSet(src: string): string {
  return SRCSET_WIDTHS.map(width => `${imagesLoaderCDN({
    quality: IMAGE_QUALITY,
    src,
    width
  })} ${String(width)}w`).join(', ');
}

// Plain <a> and <img> on purpose. With next-intl's <Link> and next/image the
// whole card subtree is serialised a second time into the RSC payload as the
// client components' props, and 25 cards of that cost ~40 KB of HTML plus 50
// components to hydrate for links that are ordinary full-page navigations.
// The href follows the same rule as <Link> (`localePrefix: 'as-needed'`): no
// prefix for the default locale, `/{locale}` otherwise. The <img> keeps what
// next/image would have emitted: lazy, async, the CDN loader's srcset and sizes.
//
// Always rooms for rent. Until 2026-09-23 the links (and the heading) followed
// the service-type cookie, so the same URL published different anchors to
// different visitors and crawlers saw whichever state their cookie jar was in.
export default function CitiesGallerySection(props) {
  const $ = _c(14);
  const {
    title
  } = props;
  const locale = useLocale();
  const t = useTranslations("home.listing");
  let t0;
  if ($[0] !== t) {
    t0 = t("title-haveshare");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const caption = t0;
  const localePrefix = locale === routing.defaultLocale ? "" : `/${locale}`;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cx(rootPageSharedStyles.section, styles.gallery);
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== title) {
    t2 = <header className={rootPageSharedStyles.header}><h2 className={rootPageSharedStyles.title}>{title}</h2></header>;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <WaveSymbolDefinition />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== caption || $[7] !== localePrefix) {
    t4 = US_GALLERY_CITIES.map(item => {
      const src = `/images/reviews/${item.img}.webp`;
      return <li key={item.slug}><a href={`${localePrefix}${ROUTER_LINKS.geoSearchResults(DEFAULT_LISTING_PART_VARIANTS.haveshare, item.slug)}`} className={styles.cityBox}><div className={styles.cityBox_wrapper}><img src={imagesLoaderCDN({
              quality: IMAGE_QUALITY,
              src,
              width: SRCSET_WIDTHS[1]
            })} srcSet={buildSrcSet(src)} sizes={IMAGE_SIZES} alt={item.city} loading="lazy" decoding="async" /><div className={styles.cityBox_waveBox}><div className={styles.cityBox_wave}><WaveSymbolUse /></div><div className={styles.cityBox_label}><span className={styles.cityBox_caption}>{caption}</span><h3 className={styles.cityBox_cityName}>{item.city}</h3></div></div></div></a></li>;
    });
    $[6] = caption;
    $[7] = localePrefix;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <ul className={styles.gallery_citiesBox}>{t4}</ul>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t5) {
    t6 = <section className={t1}>{t2}{t3}{t5}</section>;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}